'use client'

import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { AppRouterCacheProvider } from '@mui/material-nextjs/v16-appRouter'
import { HydrationBoundary } from '@tanstack/react-query'
import { SessionProvider } from 'next-auth/react'
import { SnackbarProvider } from 'notistack'
import { NuqsAdapter } from 'nuqs/adapters/next/app'

import { CartChangeTracker } from '@/components/CartChangeTracker'
import AsmSessionContextProvider from '@/providers/AsmSessionContextProvider'
import { CurrentCartProvider } from '@/providers/CurrentCartProvider'
import CustomersSearchFilterContextProvider from '@/providers/CustomersSearchFilterContextProvider'
import HeaderContextProvider from '@/providers/HeaderContextProvider'
import OrderContextProvider from '@/providers/OrderContextProvider'
import OrderHistoryFilterContextProvider from '@/providers/OrderHistoryFilterContextProvider'
import { PriceContextProvider } from '@/providers/PriceContextProvider'
import QuickSearchContextProvider from '@/providers/QuickSearchContextProvider'
import { ReactQueryClientProvider } from '@/providers/ReactQueryClientProvider'
import { SessionProviderSync } from '@/providers/SessionProviderSync'
import SiteContextProvider from '@/providers/SiteContextProvider'
import { SiteSettingsContextProvider } from '@/providers/SiteSettingsContextProvider'
import { TenantContextProvider } from '@/providers/TenantContextProvider'
import { TranslationContextProvider } from '@/providers/TranslationContextProvider'
import { commerceTheme as theme } from '@/themes/commerce/theme'

import type { Session } from 'next-auth'
import type { DehydratedState } from '@tanstack/react-query'
import type { TenantConfig } from '@/config/tenants/types'
import type { SiteSettings } from '@/interfaces/SiteSettings'

interface ProvidersProps {
  children: React.ReactNode
  session?: Session | null
  siteSettings: SiteSettings
  catalog: React.ComponentProps<typeof HeaderContextProvider>['catalog']
  serverCookies: string
  tenant: TenantConfig
  dehydratedState?: DehydratedState
}

export const Providers = ({
  children,
  session,
  siteSettings,
  catalog,
  serverCookies,
  tenant,
  dehydratedState,
}: ProvidersProps) => {
  return (
    <SessionProvider session={session} refetchOnWindowFocus={false} refetchInterval={5 * 60}>
      <SessionProviderSync />
      <AppRouterCacheProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NuqsAdapter>
            <TenantContextProvider tenant={tenant}>
              <SiteSettingsContextProvider siteSettings={siteSettings}>
                <SiteContextProvider serverCookies={serverCookies}>
                  <TranslationContextProvider
                    translations={siteSettings.translations}
                    forcedLanguage={tenant.defaultLanguage}
                  >
                    <SnackbarProvider
                      maxSnack={10}
                      hideIconVariant
                      preventDuplicate
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                      <ReactQueryClientProvider>
                        <HydrationBoundary state={dehydratedState}>
                          {/* AsmSessionContextProvider must stay above PriceContextProvider: prices are
                              enabled for employees based on the impersonation state it provides. */}
                          <AsmSessionContextProvider>
                            <CurrentCartProvider>
                              <PriceContextProvider>
                                <HeaderContextProvider catalog={catalog}>
                                  <QuickSearchContextProvider>
                                    <OrderHistoryFilterContextProvider>
                                      <CustomersSearchFilterContextProvider>
                                        <OrderContextProvider>
                                          <CartChangeTracker />
                                          {children}
                                        </OrderContextProvider>
                                      </CustomersSearchFilterContextProvider>
                                    </OrderHistoryFilterContextProvider>
                                  </QuickSearchContextProvider>
                                </HeaderContextProvider>
                              </PriceContextProvider>
                            </CurrentCartProvider>
                          </AsmSessionContextProvider>
                        </HydrationBoundary>
                      </ReactQueryClientProvider>
                    </SnackbarProvider>
                  </TranslationContextProvider>
                </SiteContextProvider>
              </SiteSettingsContextProvider>
            </TenantContextProvider>
          </NuqsAdapter>
        </ThemeProvider>
      </AppRouterCacheProvider>
    </SessionProvider>
  )
}
